/**
 * Developmental levels following Rudolf Steiner's septennial model.
 *
 * Steiner observed three great epochs of childhood, each ~7 years,
 * each governed by a different faculty and a different relationship to the world:
 *
 *   0-7   Will / Limb     "The world is good."     Imitation, movement, rhythm
 *   7-14  Feeling / Heart  "The world is beautiful." Story, art, imagination
 *   14-21 Thinking / Head  "The world is true."      Ideas, judgment, ideals
 *
 * Within the first septennial, we distinguish:
 *   Seedling  (1-3)  — toddler: sensory, rhythmic, imitative
 *   Blossom   (3-7)  — kindergarten: imaginative play, circle time, nature
 *
 * We use nature metaphors rather than clinical age labels, following the
 * Waldorf aesthetic of warmth and organic imagery.
 */

export interface DevelopmentalLevel {
  id: string;
  name: string;
  /** Steiner's motto for this epoch */
  motto: string;
  /** Nature metaphor subtitle */
  subtitle: string;
  /** Age range label */
  ages: string;
  /** Steiner faculty */
  faculty: 'will' | 'feeling' | 'thinking';
  /** Brief description for the parent/guardian */
  description: string;
  /** Whether content is available */
  available: boolean;
  /** CSS class modifier for theming */
  themeClass: string;
}

export const LEVELS: DevelopmentalLevel[] = [
  {
    id: 'seedling',
    name: 'Seedling',
    motto: 'The world is good.',
    subtitle: 'Touch, rhythm, warmth',
    ages: '1–3',
    faculty: 'will',
    description: 'Sensory exploration, rhythmic movement, and gentle imitation. The toddler learns by doing, by touching, by repeating. The world is met through the limbs.',
    available: true,
    themeClass: 'level--seedling',
  },
  {
    id: 'blossom',
    name: 'Blossom',
    motto: 'The world is good.',
    subtitle: 'Imagination, song, play',
    ages: '3–7',
    faculty: 'will',
    description: 'Imaginative play, circle time, fairy tales, and nature. The young child lives in pictures and rhythms, learning through beauty and imitation.',
    available: false,
    themeClass: 'level--blossom',
  },
  {
    id: 'weaving',
    name: 'Weaving',
    motto: 'The world is beautiful.',
    subtitle: 'Story, art, feeling',
    ages: '7–14',
    faculty: 'feeling',
    description: 'Learning through narrative, artistic practice, and the authority of a beloved teacher. The world is experienced through the heart.',
    available: false,
    themeClass: 'level--weaving',
  },
  {
    id: 'kindling',
    name: 'Kindling',
    motto: 'The world is true.',
    subtitle: 'Ideas, judgment, truth',
    ages: '14+',
    faculty: 'thinking',
    description: 'Abstract reasoning, ideals, and the search for truth. The adolescent meets the world through thinking and moral imagination.',
    available: true, // existing Weil lesson lives here
    themeClass: 'level--kindling',
  },
];
